import { useEffect, useRef, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { LanguageKeys } from 'src/common/constants/languages';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import ClaimParticipantForm from 'src/pages/Dashboard/Participants/forms/ClaimParticipantForm';
import useParticipantsStore from 'src/stores/participantsStore';
import useRegistrationsStore from 'src/stores/registrationsStore';

type ConfirmStatus = 'idle' | 'loading' | 'success' | 'error';

const PARTICIPANTS_PATH = '/dashboard/my/participants';

function useClaimToken() {
  const { search } = useLocation();
  return new URLSearchParams(search).get('token');
}

const ClaimParticipants = () => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const token = useClaimToken();
  const confirmStarted = useRef(false);

  const { initiateClaim, confirmClaim } = useParticipantsStore();
  const { fetchMyRegistrations } = useRegistrationsStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<ConfirmStatus>(token ? 'loading' : 'idle');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmedCount, setConfirmedCount] = useState(0);

  useEffect(() => {
    if (!token || confirmStarted.current) {
      return;
    }
    confirmStarted.current = true;

    const runConfirm = async () => {
      try {
        const { count } = await confirmClaim(token);
        await fetchMyRegistrations();
        setConfirmedCount(count);
        setConfirmStatus('success');
        enqueueSnackbar(t('participants.claim.confirmSuccess', { count }), { variant: 'success' });
      } catch (error: unknown) {
        const response = (error as {
          response?: { status?: number; data?: { message?: string; code?: string } };
        })?.response;
        const data = response?.data;
        const message =
          response?.status === 400
            ? t('participants.claim.confirmInvalid')
            : response?.status === 403 && data?.code === 'EMAIL_MISMATCH'
              ? t('participants.claim.confirmEmailMismatch')
              : response?.status === 403
                ? t('participants.claim.confirmForbidden')
                : data?.message || t('participants.claim.confirmFailed');
        setConfirmError(message);
        setConfirmStatus('error');
        enqueueSnackbar(message, { variant: 'error' });
      }
    };

    runConfirm();
  }, [token, confirmClaim, fetchMyRegistrations, enqueueSnackbar, t]);

  const handleInitiateClaim = async (email: string, lastName: string) => {
    setIsSubmitting(true);
    try {
      const { pending } = await initiateClaim(email, lastName, currentLang);
      setRequestSent(true);
      enqueueSnackbar(
        t('participants.claim.success', { count: pending }),
        { variant: 'success' },
      );
    } catch (error: unknown) {
      const response = (error as {
        response?: { status?: number; data?: { message?: string; code?: string } };
      })?.response;
      const data = response?.data;
      const message =
        response?.status === 404
          ? t('participants.claim.notFound')
          : response?.status === 409
            ? t('participants.claim.alreadyLocked')
            : response?.status === 403 && data?.code === 'EMAIL_MISMATCH'
              ? t('participants.claim.confirmEmailMismatch')
              : data?.message || t('participants.claim.failed');
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfirmState = () => {
    if (confirmStatus === 'loading') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">{t('participants.claim.confirming')}</Typography>
        </Box>
      );
    }

    if (confirmStatus === 'success') {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('participants.claim.confirmSuccess', { count: confirmedCount })}
          </Typography>
          <Button variant="contained" color="primary" component={RouterLink} to={PARTICIPANTS_PATH}>
            {t('participants.claim.backToParticipants')}
          </Button>
        </Box>
      );
    }

    if (confirmStatus === 'error') {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" color="error" sx={{ mb: 3 }}>
            {confirmError}
          </Typography>
          <Button variant="contained" color="primary" component={RouterLink} to={PARTICIPANTS_PATH}>
            {t('participants.claim.backToParticipants')}
          </Button>
        </Box>
      );
    }

    return null;
  };

  return (
    <DashboardListPageLayout
      title={t('participants.claim.pageTitle')}
      fill={false}
      headerActions={
        <Button
          component={RouterLink}
          to={PARTICIPANTS_PATH}
          startIcon={<ArrowBackIcon />}
          color="inherit"
        >
          {t('participants.claim.backToParticipants')}
        </Button>
      }
    >
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          maxWidth: 520,
          ...frostedPanelSx(0.5),
        }}
      >
        {token ? (
          renderConfirmState()
        ) : requestSent ? (
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('participants.claim.emailSent')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('participants.claim.emailSentHint')}
            </Typography>
            <Button variant="outlined" component={RouterLink} to={PARTICIPANTS_PATH}>
              {t('participants.claim.backToParticipants')}
            </Button>
          </Box>
        ) : (
          <ClaimParticipantForm
            handleSubmit={handleInitiateClaim}
            onCancel={() => navigate(PARTICIPANTS_PATH)}
            isSubmitting={isSubmitting}
          />
        )}
      </Paper>
    </DashboardListPageLayout>
  );
};

export default ClaimParticipants;
